import { KeyValueObj } from "@/common/types";
import { formatDate } from "@/common/utils/general";

export const requestTableHeaders = [
  "Recall Date",
  "Product",
  "Batch Number",
  "Package Type",
  "Distributors",
  "Status",
];

export const RetailerRequestTableHeaders = [
  "Recall Date",
  "Product",
  "Batch Number",
  "Package Type",
  "Status",
];

export const responseTableHeaders = [
  "Product",
  "Batch Number",
  "Status",
  "Distributor",
  "Package Type",
];

export const distributorAllRecallHeaders = [
  "Product",
  "Batch Number",
  "Manufacturer",
  "Package Type",
];

export const distributorIncomingTableHeaders = [
  "Product",
  "Batch Number",
  "Status",
  "Retailer",
  "Date Recieved",
];

export const distributorOutgoingTableHeaders = [
  "Product",
  "Batch Number",
  "Status",
  "Manufacturer",
  "Recovered(%)",
];

export const scanTableHeaders = [
  "Carton Serial",
  "Pack Serial",
  "Unit Serial",
  "Status",
];

export const requestTableKeys = [
  "recallDate",
  "product",
  "batchNumber",
  "packageType",
  "distributors",
  "status",
];

export const responseTableKeys = [
  "product",
  "batchNumber",
  "status",
  "distributors",
  "packageType",
];

export const distributorAllRecallKeys = [
  "product",
  "batchNumber",
  "manufacturer",
  "packageType",
];

export const distributorIncomingTableKeys = [
  "product",
  "batchNumber",
  "status",
  "retailer",
  "dateRecieved",
];

export const distributorOutgoingTableKeys = [
  "product",
  "batchNumber",
  "status",
  "manufacturer",
  "recovered",
];

export const scanTableKeys = [
  "cartonSerial",
  "packSerial",
  "unitSerial",
  "status",
];

export const handleScannedItem = (data: any, serial: string) => {
  return data?.map((recallData: any) => ({
    id: recallData?.id,
    cartonSerial: { type: "string", value: serial },
    packSerial: { type: "string", value: recallData?.packSerial || "NA" },
    unitSerial: { type: "string", value: recallData?.serial_number },
    status: { type: "string", value: recallData?.status },
  }));
};

export const getRequestTableData = (data: any): KeyValueObj[] => {
  if (!data || data?.length === 0) return [];
  return data?.map((item: any) => ({
    id: item?.id,
    recallDate: {
      type: "string",
      value: item?.recall_date || formatDate(item?.created_at) || "-",
    },
    product: {
      type: "string",
      value: item?.product_name,
    },
    batchNumber: {
      type: "string",
      value: item?.batch_number,
    },
    distributors: {
      type: "string",
      value: item?.affected_companies_count || "0",
    },

    status: {
      type: "string",
      value: item?.recovery_status || item?.status,
    },
    packageType: {
      type: "string",
      value: item?.packaging_level,
    },
    canDispatch: {
      type: "boolean",
      value: item?.can_dispatch ? "true" : "false",
    },
  }));
};

export function getResponseTableData(data: any): KeyValueObj[] {
  if (!data || data?.length === 0) return [];
  return data?.map((item: any) => ({
    id: item?.id,
    recallDate: { type: "string", value: item?.recall_date },
    product: {
      type: "string",
      value: item?.product_name,
    },
    batchNumber: {
      type: "string",
      value: item?.batch_number,
    },
    distributors: {
      type: "string",
      value: item?.affected_distributors_count || "0",
    },
    status: {
      type: "string",
      value: item?.status,
    },
    packageType: {
      type: "string",
      value: item?.packaging_level || "Cart",
    },
  }));
}

export const getDistributorAllRecallTableData = (data: any): KeyValueObj[] => {
  if (!data || data?.length === 0) return [];
  return data?.map((item: any) => ({
    id: item?.id,
    product: { type: "string", value: item?.product_name },
    batchNumber: { type: "string", value: item?.batch_number },
    manufacturer: { type: "string", value: item?.manufacturer_name || "NA" },
    packageType: { type: "string", value: item?.packaging_level || "N/A" },
  }));
};

export const getDistributorIncomingTableData = (
  data: KeyValueObj[],
): KeyValueObj[] => {
  if (!data || data?.length === 0) return [];
  return data?.map((item: KeyValueObj) => ({
    id: item?.id,
    product: { type: "string", value: item?.product_name },
    batchNumber: { type: "string", value: item?.batch_number },
    status: { type: "string", value: item?.recovery_status || item?.status },
    retailer: { type: "string", value: item?.retailer },
    dateRecieved: { type: "string", value: formatDate(item?.created_at) },
  }));
};

export const getDistributorOutgoingTableData = (
  data: KeyValueObj[],
): KeyValueObj[] => {
  if (data?.length === 0) return [];
  return data?.map((item: KeyValueObj) => ({
    id: item?.id,
    product: { type: "string", value: item?.product_name },
    batchNumber: { type: "string", value: item?.batch_number },
    status: { type: "string", value: item?.recovery_status || item?.status },
    manufacturer: { type: "string", value: item?.manufacturer },
    recovered: {
      type: "string",
      value: item?.recovered_percentage
        ? `${item?.recovered_percentage}%`
        : "0%",
    },
  }));
};

export const handleProduct = (data: any) => {
  if (data?.length === 0) return [];
  const updatedData = data?.map((item: any) => ({
    title: item?.name,
    name: item?.name,
  }));
  return updatedData;
};

export const getProductData = (data: KeyValueObj) => [
  {
    title: "Product Name",
    value:
      data?.recalled_products?.[0]?.inventory?.product?.brand_name ||
      data?.recalled_products?.[0]?.product?.brand_name ||
      "Not Provided",
  },
  {
    title: "Batch ID",
    value: data?.recalled_products
      ? data?.recalled_products?.[0]?.batch_number
      : data?.batch_number || "Not provided",
  },
  {
    title: "Package Type",
    value:
      data?.recalled_products?.[0]?.packaging_level ||
      data?.product?.tertiary_packaging ||
      "Not provided",
  },
  {
    title: "Manufacture Date",
    value:
      data?.recalled_products?.[0]?.inventory?.manufacture_date ||
      "Not provided",
  },
  {
    title: "Expiry Date",
    value:
      data?.recalled_products?.[0]?.inventory?.expiry_date ||
      data?.inventory?.expiry_date ||
      "Not provided",
  },
  {
    title: "Stock Quantity",
    value:
      data?.recalled_products?.[0]?.inventory?.quantity_in_stock ||
      "Not provided",
  },
];

export const getCartonData = (data: any) => {
  const cartonData = data?.recalled_products;
  return cartonData?.map((item: any) => item?.serial_number);
};

export const dummyDistributors = [
  {
    name: "Nana-aisha-INC",
    value: "20,000",
    response: "pending",
  },
  {
    name: "Quado Limited",
    value: "30,000",
    response: "recieved",
  },
  {
    name: "MedTrust Limited",
    value: "3,000",
    response: "pending",
  },
  {
    name: "Galapagous Limited",
    value: "10,000",
    response: "recieved",
  },
  {
    name: "Sukky Limited",
    value: "3,000",
    response: "pending",
  },
];

export const getActiveCartonData = (cartonSerial: string, data: any) => {
  const cartonData = data?.carton_units?.find(
    (item: any) => item?.serial_number === cartonSerial,
  );
  const packData = data?.pack_units?.filter(
    (item: any) => item?.carton_unit_id !== cartonSerial,
  );

  const unitSerial =
    packData?.length > 0
      ? packData?.map((pack: any) => ({
          pack_number: pack?.id,
          serial_number: data?.unit_items?.filter(
            (unit: any) => unit?.pack_unit_id === pack?.id,
          ),
        }))
      : [];

  return {
    serial_number: cartonSerial,
    data: { ...cartonData, pack_number: packData, unit_serial: unitSerial },
  };
};

export const recallReasonInputs = [
  {
    inputType: "text",
    prompt: "Reason For Recall",
    name: "reason",
    placeholder: "Enter comment",
    fullWidth: true,
    rules: {
      required: true,
    },
  },
  {
    inputType: "date",
    prompt: "Expected Completion Date",
    name: "expected_completion_date",
    // placeholder: "Enter Note",
    fullWidth: true,
    rules: {
      required: true,
    },
  },
  {
    inputType: "text",
    prompt: "Regulatory Note (optional)",
    name: "regulatory_note",
    placeholder: "Enter Note",
    fullWidth: true,
  },
];

export const recallFilterOptions = [
  {
    name: "status",
    label: "Recall Status",
    type: "select",
    options: [
      {
        label: "Resolved",
        value: "resolved",
      },
      {
        label: "Active",
        value: "active",
      },
    ],
  },
  {
    name: "date",
    label: "Creation Date",
    type: "date",
  },
];
